import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Calculate } from '@mui/icons-material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { CurrencyField } from '@/components/common/CurrencyField'
import {
  CODIGOS_IDEMPOTENCIA,
  MENSAGEM_RESERVA_NAO_SINCRONIZADA,
  mensagemErroSimulacao,
  simulacaoComissaoService,
} from '@/services/simulacaoComissaoService'
import { CODIGO_CAMPANHA_INCENTIVO, CODIGO_OUTRA_NECESSIDADE } from '@/constants/codigosProduto'
import { ROTULO_OUTRA_NECESSIDADE, reservaEhOutraNecessidade } from '@/utils/outraNecessidade'
import { politicasSimulacaoService } from '@/services/politicasSimulacaoService'
import { produtoService } from '@/services/produtoService'
import { oportunidadeService } from '@/services/oportunidadeService'
import type { RequisicaoFalhou } from '@/services/api'
import type {
  FaixaComissao,
  ModeloSimulacao,
  MotivoBloqueio,
  MotivoBloqueioDetalhe,
  MotivoSemFormula,
  PassoCalculo,
  PoliticaAplicada,
  PoliticaComissaoResponse,
  PoliticaProdutoVigente,
  ResponsavelCorrecao,
  SimulacaoComissaoRequest,
  SimulacaoComissaoResponse,
} from '@/types/simulacaoComissao'
import { ALERTA_CAMPANHA_NAO_APLICAVEL, ROTULO_MODELO } from '@/types/simulacaoComissao'
import type { Produto } from '@/types/produto'
import { categoriaRetirada } from '@/constants/categoriasProduto'
import type { Oportunidade } from '@/types/oportunidade'
import { STATUS_LABEL } from '@/types/statusReserva'
import { formatDateTime } from '@/utils/formatters'
import { SIMULADOR_HABILITADO } from '@/config/features'
import { brl, lerPercentualPtBr, pctDeFracao, percentualParaFracao } from './fracao'
import { elegibilidadeReservaService } from '@/services/elegibilidadeReservaService'
import { CODIGOS_ELEGIBILIDADE } from '@/types/elegibilidade'
import { ChipRotaOperacao } from '@/components/elegibilidade/ChipElegibilidade'

/**
 * Simulações do consultor (Figma 26:270) — Rodada 2 / DEV-5, sobre o motor unificado
 * (`POST /v1/simulacoes/comissao`, D2/D3 do bloco 8).
 *
 * - **Por reserva:** a pessoa escolhe uma reserva da própria carteira; produto e parceiro
 *   vêm da reserva (projeção no módulo `simulacao`) e são só leitura — a tela nunca os
 *   envia nem os edita. Sem parceiro, sem política vigente, reserva encerrada ou "outra
 *   necessidade" → o backend responde BLOQUEADA com o motivo, e a tela explica.
 * - **Campanha de Incentivo:** sem reserva, valor + taxa negociada (percentual na UI,
 *   fração na API).
 * - Nenhum número é calculado aqui: faixa, receita, bruta, tributos e líquido chegam
 *   prontos e versionados. A flag `SIMULADOR_HABILITADO` governa só o simulador de
 *   crédito/Price legado (`?aba=simulador`), não esta simulação.
 * - "Regras por produto" lê as políticas vigentes (`/politicas/produtos/vigentes`)
 *   cruzadas com o catálogo; sem política o produto aparece como bloqueado para
 *   revisão administrativa — sem nenhuma versão ou linha fixa em código.
 *
 * O aviso "estimativa indicativa" é obrigatório em toda simulação calculada: o
 * Financeiro é a fonte do valor confirmado, conciliado e pago (6.1/D3).
 *
 * Decisões de implementação 20/10/2026:
 * - sem alíquota vigente a bruta aparece e o líquido fica "Pendente de configuração
 *   tributária", com quem corrige (§1.2/§7) — nunca "R$ 0,00" nem a bruta no lugar;
 * - CAPEX mostra a mensagem normativa da §5.1, vinda do backend (snapshot antigo da Duplicata
 *   Escritural, RETIRADA em 25/10, continua legível com a mensagem que gravou);
 * - alertas de elegibilidade (faturamento, LTV) acompanham o cálculo e não o bloqueiam (§4);
 * - modelos 360 não mostram faixa de volume (§7); a versão do spread e a data de referência
 *   entram nas versões exibidas;
 * - toda troca de entrada descarta o resultado anterior (§7: nada de valor antigo na tela);
 * - cada intenção de simular leva uma `Idempotency-Key`: o reenvio (falha de rede, clique
 *   repetido) devolve o mesmo snapshot em vez de duplicar (§9.2).
 *
 * Onda P0 das Políticas por Produto (PDF 25/10/2026):
 * - produto RETIRADO (Duplicata Escritural) bloqueia com `PRODUTO_RETIRADO` e a mensagem do backend;
 * - `dataReferencia` futura ou anterior à criação da reserva → 400 `DATA_REFERENCIA_INVALIDA`, mostrado
 *   no campo da data, legível (dd/mm/aaaa); o seletor já não oferece data futura (max = hoje);
 * - o alerta `CAMPANHA_NAO_APLICAVEL` (a política do produto prevaleceu sobre a Campanha) aparece como
 *   os demais alertas, separado dos de elegibilidade.
 *
 * Onda P4 das Políticas por Produto (AN-PP4 §12 F2) — comissão como política versionada:
 * - "Como foi calculado" exibe os `passos[]` do backend (snapshot antigo sem passos: `memoriaCalculo`);
 * - o bloqueio lista TODOS os `motivosBloqueio[]` (mensagem, quem corrige, o que fazer); o primeiro dá
 *   o título; lista vazia (snapshot antigo) cai para `motivoBloqueio`/`mensagemBloqueio`;
 * - 360: rótulo "Receita líquida Originei", valor da operação opcional (vazio = nulo), parceiro não exigido;
 * - "Faixas vigentes" lê `GET /politicas/comissao/vigente` (404 = sem política vigente); o percentual do
 *   360 vem dela — nenhum número de faixa ou percentual literal na tela;
 * - a linha da Campanha em "Regras por produto" vem só da política vigente do INCENTIVE_CAMPAIGN;
 * - `Idempotency-Key` obrigatória: 400 `IDEMPOTENCIA_*` mostra a mensagem e descarta a chave.
 */

export const AVISO_INDICATIVA = 'Estimativa indicativa, sujeita ao aceite do Financeiro.'
export const SEM_FAIXA = 'Sem faixa aplicada'
export const REGRA_VIGENTE = 'Regra vigente'
export const POLITICA_VIGENTE = 'Política vigente'
export const SEM_POLITICA_VIGENTE = 'Sem política vigente — revisão administrativa'
export const TITULO_BLOQUEIO_PARCEIRO = 'Simulação disponível após a Originei definir o parceiro'
export const TITULO_RESERVA_NAO_SINCRONIZADA = 'Reserva ainda não sincronizada com a Simulação'
export const TITULO_BLOQUEIO_POLITICA = 'Simulação bloqueada — revisão administrativa'
export const TITULO_RESERVA_ENCERRADA = 'Reserva encerrada — não há comissão a estimar'
export const TITULO_OUTRA_NECESSIDADE = 'Outra necessidade — sem simulação'
/** Texto normativo da §5.1 das Decisões de 20/10 (o backend manda o mesmo). */
export const MENSAGEM_OUTRA_NECESSIDADE =
  'Esta necessidade depende de análise da Originei e não possui simulação automática.'
export const SOLICITAR_REVISAO = 'Solicitar revisão administrativa'
export const TITULO_SIMULACAO_INDISPONIVEL = 'Simulação indisponível'
export const LIQUIDO_PENDENTE = 'Pendente de configuração tributária'
export const TITULO_ALERTAS_ELEGIBILIDADE = 'Alertas de elegibilidade'
export const TITULO_PRODUTO_RETIRADO = 'Produto retirado do catálogo'
export const TITULO_ALERTAS_POLITICA = 'A política do produto prevaleceu'
// ── P4 ──
export const TITULO_FAIXAS_VIGENTES = 'Faixas vigentes'
export const SEM_POLITICA_COMISSAO = 'Sem política de comissão vigente'
/** Texto de reserva do bloqueio `POLITICA_COMISSAO_AUSENTE` quando o backend não manda mensagem. */
export const MENSAGEM_POLITICA_COMISSAO_AUSENTE =
  'Não há política de comissão vigente na data de referência: as faixas e o percentual do 360 precisam ser publicados pela Originei. Nada é assumido.'
export const TITULO_COMO_CALCULADO = 'Como foi calculado'
export const ROTULO_RETENCOES = 'Retenções (indicativas)'
export const ROTULO_RECEITA_LIQUIDA = 'Receita líquida Originei'
export const PARCEIRO_NAO_EXIGIDO = 'Não exigido no modelo 360'
export const TITULO_OUTROS_MOTIVOS = 'Também impede o cálculo'

/** `responsavel` (enum) → texto, só quando o backend não mandou `responsavelCorrecao`. */
const ROTULO_RESPONSAVEL: Record<ResponsavelCorrecao, string | null> = {
  ORIGINEI: 'Originei',
  ADMINISTRADOR: 'Administrador',
  FINANCEIRO: 'Financeiro',
  NENHUM: null,
}

/**
 * Motivos SEM_FORMULA com mensagem normativa própria (§5.1) — CAPEX; o segundo motivo era o da
 * Duplicata Escritural (RETIRADA) e fica só para ler snapshot antigo.
 */
const MOTIVOS_INDISPONIVEL: ReadonlySet<string> = new Set<MotivoSemFormula>([
  'POLITICA_COMERCIAL_EM_DEFINICAO',
  'REGRA_COMISSIONAMENTO_NAO_APROVADA',
])

type Modo = 'reserva' | 'campanha'
type CampoSimulacao =
  | 'valorOperacao'
  | 'taxaNegociada'
  | 'receitaLiquidaOriginei'
  | 'dataReferencia'
  | 'dadosElegibilidade.faturamentoAnual'
  | 'dadosElegibilidade.valorImovel'
  | 'dadosElegibilidade.valorSolicitado'

/** Formato esperado por campo, na unidade que a pessoa digita (S4). */
const FORMATO_CAMPO: Record<CampoSimulacao, string> = {
  valorOperacao: 'Formato: R$ 0,00 (zero é permitido)',
  taxaNegociada: 'Formato: percentual com vírgula, ex.: 2,5',
  receitaLiquidaOriginei: 'Formato: R$ 0,00',
  dataReferencia: 'Formato: data (dd/mm/aaaa); vazio = hoje',
  'dadosElegibilidade.faturamentoAnual': 'Formato: R$ 0,00 (vazio = não informado)',
  'dadosElegibilidade.valorImovel': 'Formato: R$ 0,00 (vazio = não informado)',
  'dadosElegibilidade.valorSolicitado': 'Formato: R$ 0,00 (vazio = não informado)',
}

/** Chave de idempotência por intenção de simular (§9.2). */
const novaChave = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `sim-${Date.now()}-${Math.random().toString(16).slice(2)}`

const CAMPO_POR_CODIGO: Partial<Record<string, CampoSimulacao>> = {
  TAXA_NEGOCIADA_OBRIGATORIA: 'taxaNegociada',
  RECEITA_LIQUIDA_OBRIGATORIA: 'receitaLiquidaOriginei',
  DATA_REFERENCIA_INVALIDA: 'dataReferencia',
  VALOR_OPERACAO_OBRIGATORIO: 'valorOperacao',
}

/** Hoje no fuso do navegador, ISO `YYYY-MM-DD` — limite do seletor (nada é calculado com ela). */
const hojeIso = (): string => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const rotuloReserva = (o: Oportunidade): string => {
  const numero = o.numero != null ? `RES-${String(o.numero).padStart(4, '0')}` : o.id.slice(0, 8)
  const empresa = o.razaoSocial ?? o.nomeFantasia ?? o.nomeCliente ?? 'Empresa não informada'
  return `${numero} · ${empresa}`
}

const ehStatus403 = (e: unknown) => (e as { status?: number })?.status === 403

interface LinhaRegra {
  chave: string
  produto: string
  modelo: string
  natureza: string
  versao: string
  origem: string
  situacao: 'campanha' | 'vigente' | 'ausente' | 'indisponivel'
}

const SimulacoesPage: FC = () => {
  const navigate = useNavigate()

  // ── base: reservas do consultor + políticas vigentes + catálogo ──
  const [reservas, setReservas] = useState<Oportunidade[] | null>(null)
  const [erroReservas, setErroReservas] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)
  const [vigentes, setVigentes] = useState<PoliticaProdutoVigente[] | null>(null)
  const [erroVigentes, setErroVigentes] = useState<string | null>(null)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [carregandoBase, setCarregandoBase] = useState(true)
  /** P4: política de comissão vigente — `undefined` carregando, `null` sem vigente (404). */
  const [comissaoVigente, setComissaoVigente] = useState<PoliticaComissaoResponse | null | undefined>(undefined)
  const [erroComissao, setErroComissao] = useState<string | null>(null)

  // ── entradas ──
  const [modo, setModo] = useState<Modo>('reserva')
  const [reserva, setReserva] = useState<Oportunidade | null>(null)
  const [valor, setValor] = useState<number>(0)
  const [taxa, setTaxa] = useState('')
  const [receitaLiquida, setReceitaLiquida] = useState<number>(0)
  /**
   * Elegibilidade (§4): só alerta. 0 = não informado. Fix PP2 (B5): o LTV vai como VALOR DO IMÓVEL e VALOR
   * SOLICITADO (o servidor calcula e compara exato, igual à reserva) — nunca um percentual arredondado.
   */
  const [faturamento, setFaturamento] = useState<number>(0)
  const [valorImovel, setValorImovel] = useState<number>(0)
  const [valorSolicitado, setValorSolicitado] = useState<number>(0)
  /** ISO `YYYY-MM-DD`; vazio = hoje (o backend resolve). */
  const [dataReferencia, setDataReferencia] = useState('')
  /** Intenção de simular em curso: mesma requisição → mesma chave até dar certo (§9.2). */
  const intencao = useRef<{ assinatura: string; chave: string } | null>(null)

  // ── saída ──
  const [simulando, setSimulando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [errosCampo, setErrosCampo] = useState<Partial<Record<CampoSimulacao, string>>>({})
  const [resultado, setResultado] = useState<SimulacaoComissaoResponse | null>(null)
  const [historico, setHistorico] = useState<SimulacaoComissaoResponse[] | null>(null)
  const [carregandoHistorico, setCarregandoHistorico] = useState(false)
  /**
   * O backend pediu a receita líquida (RECEITA_LIQUIDA_OBRIGATORIA) para uma reserva cujo
   * modelo a tela não conhecia (políticas vigentes fora do ar, ou política cadastrada
   * depois do carregamento): o campo passa a ser renderizado com o erro — nunca um erro
   * num campo invisível (R01 #2).
   */
  const [receitaExigidaPeloBackend, setReceitaExigidaPeloBackend] = useState(false)

  const carregarReservas = useCallback(async () => {
    setErroReservas(null)
    try {
      const pagina = await oportunidadeService.listar(0, 200)
      setReservas(pagina?.content ?? [])
    } catch (e) {
      if (ehStatus403(e)) setSemPermissao(true)
      setReservas(null)
      setErroReservas((e as { message?: string })?.message ?? 'Não foi possível carregar as reservas')
    }
  }, [])

  const carregarVigentes = useCallback(async () => {
    setErroVigentes(null)
    try {
      setVigentes(await politicasSimulacaoService.listarProdutosVigentes())
    } catch (e) {
      if (ehStatus403(e)) setSemPermissao(true)
      setVigentes(null)
      setErroVigentes((e as { message?: string })?.message ?? 'Não foi possível carregar as políticas vigentes')
    }
  }, [])

  useEffect(() => {
    let ativo = true
    setCarregandoBase(true)
    Promise.all([
      carregarReservas(),
      carregarVigentes(),
      // Catálogo: melhor esforço — sem ele a tabela lista só o que tem política vigente.
      produtoService
        .listar(0, 200)
        .then((p) => {
          if (ativo) setProdutos(p?.content ?? [])
        })
        .catch(() => undefined),
    ]).finally(() => {
      if (ativo) setCarregandoBase(false)
    })
    return () => {
      ativo = false
    }
  }, [carregarReservas, carregarVigentes])

  // P4: faixas vigentes na data de referência escolhida (vazio = hoje no servidor).
  const carregarComissao = useCallback(async (data: string, ativo: () => boolean = () => true) => {
    setErroComissao(null)
    setComissaoVigente(undefined)
    try {
      const vigente = await politicasSimulacaoService.comissaoVigente(data || undefined)
      if (ativo()) setComissaoVigente(vigente)
    } catch (e) {
      if (!ativo()) return
      setComissaoVigente(null)
      setErroComissao(
        (e as { message?: string })?.message ?? 'Não foi possível carregar a política de comissão vigente'
      )
    }
  }, [])
  useEffect(() => {
    let ativo = true
    void carregarComissao(dataReferencia, () => ativo)
    return () => {
      ativo = false
    }
  }, [carregarComissao, dataReferencia])

  // "Simular" no detalhe da reserva chega com `?reserva=<id>` (Rodada 3): quando a
  // listagem carrega, pré-seleciona essa reserva no modo "Por reserva" — uma vez só, e só
  // se ela estiver na carteira listada (id desconhecido = tela como sem o parâmetro).
  const [searchParams] = useSearchParams()
  const reservaPreSelecionadaId = searchParams.get('reserva')
  const preSelecaoAplicada = useRef(false)
  useEffect(() => {
    if (preSelecaoAplicada.current || !reservaPreSelecionadaId || !reservas) return
    preSelecaoAplicada.current = true
    const alvo = reservas.find((r) => r.id === reservaPreSelecionadaId)
    if (alvo) {
      setModo('reserva')
      setReserva(alvo)
    }
  }, [reservaPreSelecionadaId, reservas])

  const carregarHistorico = useCallback(async (oportunidadeId: string) => {
    setCarregandoHistorico(true)
    try {
      setHistorico(await simulacaoComissaoService.listarPorReserva(oportunidadeId))
    } catch {
      setHistorico([])
    } finally {
      setCarregandoHistorico(false)
    }
  }, [])

  useEffect(() => {
    setResultado(null)
    setErro(null)
    setErrosCampo({})
    setReceitaExigidaPeloBackend(false)
    if (modo === 'reserva' && reserva) void carregarHistorico(reserva.id)
    else setHistorico(null)
  }, [modo, reserva, carregarHistorico])

  /**
   * P2 (AN-PP2 §8): em modo reserva, faturamento e LTV vêm PRÉ-PREENCHIDOS com os dados
   * declarados da reserva (`GET /v1/oportunidades/{id}/elegibilidade` — o front lê os dois
   * serviços; nenhuma chamada entre serviços). O LTV é o `ltvCalculado` do servidor, só mudando a
   * unidade (fração → %). Sem snapshot ou com falha, os campos ficam como estão (opcionais).
   */
  const [preenchidoDaReserva, setPreenchidoDaReserva] = useState(false)
  useEffect(() => {
    setPreenchidoDaReserva(false)
    if (modo !== 'reserva' || !reserva) return
    let ativo = true
    elegibilidadeReservaService
      .obter(reserva.id)
      .then((e) => {
        if (!ativo || !e) return
        // Dados declarados VIGENTES (`dadosDeclarados`); a última avaliação é o fallback. O solicitado vem já
        // efetivo (sem declaração = valor da operação) e só entra junto com o imóvel (é o par do LTV).
        const fat = e.dadosDeclarados?.faturamentoAnual ?? e.ultima?.faturamentoAnual ?? null
        const imovel = e.dadosDeclarados?.valorImovel ?? e.ultima?.valorImovel ?? null
        const solicitado = imovel == null ? null : e.dadosDeclarados?.valorSolicitado ?? e.ultima?.valorSolicitado ?? null
        if (fat != null) setFaturamento(fat)
        if (imovel != null) setValorImovel(imovel)
        if (solicitado != null) setValorSolicitado(solicitado)
        setPreenchidoDaReserva(fat != null || imovel != null)
      })
      .catch(() => {
        // Sem os dados declarados a simulação segue: os campos são opcionais (só alertas).
      })
    return () => {
      ativo = false
    }
  }, [modo, reserva])

  // §7: valor antigo nunca fica na tela depois de mudar uma entrada (inclusive zerar o valor).
  useEffect(() => {
    setResultado(null)
  }, [valor, taxa, receitaLiquida, faturamento, valorImovel, valorSolicitado, dataReferencia])

  // Política vigente do produto da reserva (`produtoId` vem na listagem desde 8511425) —
  // decide se o campo "Receita líquida" aparece antes de simular.
  const politicaDaReserva = useMemo(
    () =>
      reserva?.produtoId && vigentes
        ? vigentes.find((v) => v.produtoId === reserva.produtoId) ?? null
        : null,
    [reserva, vigentes]
  )
  /**
   * P1 (AN-PP1 §3.1): reserva do produto Campanha de Incentivo (pelo CÓDIGO) mostra direto o
   * formulário da Campanha — taxa negociada editável — e envia `modelo=CAMPANHA` com a reserva.
   */
  const reservaCampanha = modo === 'reserva' && reserva?.produtoCodigo === CODIGO_CAMPANHA_INCENTIVO
  const comTaxaNegociada = modo === 'campanha' || reservaCampanha
  const modelo360 =
    modo === 'reserva' &&
    !reservaCampanha &&
    (politicaDaReserva?.modelo === 'RECEITA_LIQUIDA_360' ||
      resultado?.modelo === 'RECEITA_LIQUIDA_360' ||
      receitaExigidaPeloBackend)

  /** Campos que estão na tela agora — erro em campo invisível vira erro geral ou força o campo. */
  const campoVisivel = (campo: CampoSimulacao): boolean =>
    campo === 'valorOperacao' ||
    campo === 'dataReferencia' ||
    (campo === 'taxaNegociada' && comTaxaNegociada) ||
    (campo === 'receitaLiquidaOriginei' && modelo360) ||
    (campo.startsWith('dadosElegibilidade.') && modo === 'reserva')

  const podeSimular =
    !simulando &&
    (modo === 'campanha' ? taxa.trim() !== '' : reserva !== null && (!reservaCampanha || taxa.trim() !== ''))

  const simular = async () => {
    setSimulando(true)
    setErro(null)
    setErrosCampo({})
    try {
      let request: SimulacaoComissaoRequest
      if (modo === 'campanha') {
        const taxaPercentual = lerPercentualPtBr(taxa)
        if (taxaPercentual == null) {
          setErrosCampo({ taxaNegociada: `Taxa negociada inválida. ${FORMATO_CAMPO.taxaNegociada}` })
          return
        }
        request = {
          modelo: 'CAMPANHA',
          valorOperacao: valor,
          taxaNegociada: percentualParaFracao(taxaPercentual),
        }
      } else {
        if (!reserva) return
        let campanhaDaReserva: Pick<SimulacaoComissaoRequest, 'modelo' | 'taxaNegociada'> = {}
        if (reservaCampanha) {
          const taxaPercentual = lerPercentualPtBr(taxa)
          if (taxaPercentual == null) {
            setErrosCampo({ taxaNegociada: `Taxa negociada inválida. ${FORMATO_CAMPO.taxaNegociada}` })
            return
          }
          campanhaDaReserva = { modelo: 'CAMPANHA', taxaNegociada: percentualParaFracao(taxaPercentual) }
        }
        const temElegibilidade = faturamento > 0 || valorImovel > 0 || valorSolicitado > 0
        request = {
          oportunidadeId: reserva.id,
          // P4 (Q72): no 360 o valor é opcional — vazio (0) vai nulo; não altera a comissão.
          valorOperacao: modelo360 && valor === 0 ? null : valor,
          ...campanhaDaReserva,
          ...(modelo360 ? { receitaLiquidaOriginei: receitaLiquida } : {}),
          ...(temElegibilidade
            ? {
                dadosElegibilidade: {
                  ...(faturamento > 0 ? { faturamentoAnual: faturamento } : {}),
                  ...(valorImovel > 0 ? { valorImovel } : {}),
                  ...(valorSolicitado > 0 ? { valorSolicitado } : {}),
                },
              }
            : {}),
        }
      }
      if (dataReferencia) request = { ...request, dataReferencia }
      // Mesma requisição reenviada (falha de rede, clique repetido) → mesma chave; outra → nova.
      const assinatura = JSON.stringify(request)
      if (intencao.current?.assinatura !== assinatura) {
        intencao.current = { assinatura, chave: novaChave() }
      }
      const r = await simulacaoComissaoService.simular(request, intencao.current.chave)
      intencao.current = null
      setResultado(r)
      if (modo === 'reserva' && reserva) void carregarHistorico(reserva.id)
    } catch (e) {
      const falha = e as RequisicaoFalhou
      const corpo = falha?.data as
        | { errorCode?: string; data?: Record<string, string>; message?: string }
        | undefined
      // P4: chave recusada (ausente, inválida ou já usada por outra requisição) — o próximo envio
      // é outra intenção, com chave nova; nada foi gravado.
      if (corpo?.errorCode && CODIGOS_IDEMPOTENCIA.has(corpo.errorCode)) intencao.current = null
      // Erro dirigido a um campo: só fica no campo se ele estiver na tela. Receita líquida
      // pedida sem a tela saber que o modelo é 360 → o campo passa a ser renderizado com o
      // erro; qualquer outro campo invisível → Alert geral. Nunca silêncio.
      const atribuir = (campo: CampoSimulacao, msg: string): string | null => {
        if (campoVisivel(campo)) return null
        if (campo === 'receitaLiquidaOriginei' && modo === 'reserva') {
          setReceitaExigidaPeloBackend(true)
          return null
        }
        return `${campo}: ${msg}`
      }
      if (corpo?.errorCode === 'VALIDATION_ERROR' && corpo.data && typeof corpo.data === 'object') {
        const porCampo: Partial<Record<CampoSimulacao, string>> = {}
        const soltos: string[] = []
        for (const [campo, msg] of Object.entries(corpo.data)) {
          if (campo in FORMATO_CAMPO) {
            const c = campo as CampoSimulacao
            const texto = `${msg}. ${FORMATO_CAMPO[c]}`
            const solto = atribuir(c, msg)
            if (solto) soltos.push(solto)
            else porCampo[c] = texto
          } else {
            soltos.push(`${campo}: ${msg}`)
          }
        }
        setErrosCampo(porCampo)
        if (soltos.length) setErro(soltos.join(' · '))
        return
      }
      const campo = corpo?.errorCode ? CAMPO_POR_CODIGO[corpo.errorCode] : undefined
      if (campo) {
        // Sem o ponto final do backend: o formato do campo vem logo depois.
        const msg = (mensagemErroSimulacao(e) ?? corpo?.message ?? 'Campo obrigatório').replace(/\.\s*$/, '')
        const solto = atribuir(campo, msg)
        if (solto) setErro(solto)
        else setErrosCampo({ [campo]: `${msg}. ${FORMATO_CAMPO[campo]}` })
        return
      }
      setErro(mensagemErroSimulacao(e) ?? falha?.message ?? 'Não foi possível simular.')
    } finally {
      setSimulando(false)
    }
  }

  // ── "Regras por produto": políticas vigentes × catálogo ──
  const linhas = useMemo<LinhaRegra[]>(() => {
    const porProduto = new Map((vigentes ?? []).map((v) => [v.produtoId, v]))
    // P1 (AN-PP1 §3.1): a Campanha de Incentivo virou produto do catálogo (INCENTIVE_CAMPAIGN) —
    // a linha fixa da Campanha passa a mostrar a política vigente dele, quando houver, e o
    // produto não se repete como linha do catálogo.
    // P4 (AN-PP4 §5.2, fim da síntese D14): a linha vem SÓ da política vigente — nunca da última
    // simulação. Sem catálogo, a política de modelo CAMPANHA ainda é reconhecida pelo modelo.
    const produtoCampanha = produtos.find((p) => p.codigo === CODIGO_CAMPANHA_INCENTIVO)
    const politicaCampanha = produtoCampanha
      ? porProduto.get(produtoCampanha.id)
      : (vigentes ?? []).find((v) => v.modelo === 'CAMPANHA')
    if (produtoCampanha) porProduto.delete(produtoCampanha.id)
    if (politicaCampanha) porProduto.delete(politicaCampanha.produtoId)
    const campanha: LinhaRegra = {
      chave: 'campanha',
      produto: 'Campanha de Incentivo',
      modelo: ROTULO_MODELO.CAMPANHA,
      natureza: politicaCampanha?.natureza ?? '—',
      versao: politicaCampanha?.versao ?? '—',
      origem: politicaCampanha?.origem ?? '—',
      situacao: politicaCampanha ? 'campanha' : 'ausente',
    }
    // As políticas de produto desta tabela são as vigentes HOJE (`/produtos/vigentes`). O percentual
    // do 360 só entra no rótulo quando a política de comissão carregada também é a de hoje (data de
    // referência vazia) — nunca mistura a comissão de outra data numa tabela de hoje.
    const comissaoDeHoje = dataReferencia ? null : comissaoVigente
    const rotuloModelo = (m: ModeloSimulacao): string =>
      m === 'RECEITA_LIQUIDA_360' && comissaoDeHoje
        ? `${pctDeFracao(comissaoDeHoje.percentualReceitaLiquida360)} da receita líquida Originei (sem faixa)`
        : ROTULO_MODELO[m] ?? m
    // Categoria RETIRADA (Duplicata Escritural — Políticas Vigentes 25/10, seção 8) sai da tabela:
    // nem como linha do catálogo nem como política "fora do catálogo". O histórico fica nos snapshots.
    // P1: idem para status RETIRADA; "Outra necessidade" (OTHER_NEED) não tem simulação nem política.
    const foraDaTabela = (p: Produto) =>
      categoriaRetirada(p.categoria) ||
      p.status === 'RETIRADA' ||
      p.codigo === CODIGO_OUTRA_NECESSIDADE ||
      p.codigo === CODIGO_CAMPANHA_INCENTIVO
    const retirados = produtos.filter(foraDaTabela)
    retirados.forEach((p) => porProduto.delete(p.id))
    const doCatalogo: LinhaRegra[] = produtos.filter((p) => !foraDaTabela(p)).map((p) => {
      const politica = porProduto.get(p.id)
      porProduto.delete(p.id)
      if (!politica && p.status === 'SEM_POLITICA_SIMULACAO') {
        // CAPEX (AN-PP1 §5): status normativo "Simulação indisponível" — não é falha a revisar.
        return {
          chave: p.id,
          produto: p.nome,
          modelo: '—',
          natureza: '—',
          versao: '—',
          origem: '—',
          situacao: 'indisponivel',
        }
      }
      return politica
        ? {
            chave: p.id,
            produto: p.nome,
            modelo: rotuloModelo(politica.modelo),
            natureza: politica.natureza ?? '—',
            versao: politica.versao,
            origem: politica.origem,
            situacao: 'vigente',
          }
        : {
            chave: p.id,
            produto: p.nome,
            modelo: '—',
            natureza: '—',
            versao: '—',
            origem: '—',
            situacao: 'ausente',
          }
    })
    // Política vigente de produto que o catálogo não trouxe (catálogo fora ou produto removido).
    const foraDoCatalogo: LinhaRegra[] = [...porProduto.values()].map((v) => ({
      chave: v.produtoId,
      produto: v.produtoNome,
      modelo: rotuloModelo(v.modelo),
      natureza: v.natureza ?? '—',
      versao: v.versao,
      origem: v.origem,
      situacao: 'vigente',
    }))
    return [campanha, ...doCatalogo, ...foraDoCatalogo]
  }, [produtos, vigentes, comissaoVigente, dataReferencia])

  const cabecalho = (
    <PageHeader
      title="Simulações"
      subtitle="Estimativa indicativa da comissão — o Financeiro é a fonte do valor pago"
      action={
        SIMULADOR_HABILITADO ? (
          <Button variant="outlined" onClick={() => navigate('/simulacoes?aba=simulador')}>
            Simulador de operações
          </Button>
        ) : undefined
      }
    />
  )

  if (semPermissao) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="semPermissao"
          detalhe="A simulação de comissão é do consultor e do back-office da Originei."
        />
      </Box>
    )
  }

  const calculada = resultado?.estado === 'CALCULADA' ? resultado : null
  const liquidoPendente = calculada != null && ehLiquidoPendente(calculada)

  return (
    <Box>
      {cabecalho}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* KPIs: rótulos do último resultado do backend; nada calculado aqui */}
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Produto"
            valor={
              resultado
                ? resultado.modelo === 'CAMPANHA'
                  ? 'Campanha de Incentivo'
                  : resultado.produtoNome ?? '—'
                : '—'
            }
            legenda={resultado?.natureza ?? 'Escolha a reserva ou a Campanha'}
            variante="escura"
            carregando={carregandoBase}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Operação"
            valor={brl(resultado?.valorOperacao)}
            legenda="Valor informado"
            carregando={carregandoBase}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Spread"
            valor={pctDeFracao(resultado?.spread)}
            legenda={
              resultado
                ? resultado.spread != null
                  ? `Política ${resultado.versaoPolitica ?? '—'}`
                  : 'Modelo sem spread'
                : 'Vem da política do parceiro'
            }
            carregando={carregandoBase}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Comissão"
            valor={brl(liquidoPendente ? calculada?.comissaoBruta : calculada?.valorLiquido)}
            legenda={
              !calculada
                ? '—'
                : liquidoPendente
                  ? `Bruta indicativa · líquido ${LIQUIDO_PENDENTE.toLowerCase()}`
                  : `Líquida indicativa · bruta ${brl(calculada.comissaoBruta)}`
            }
            carregando={carregandoBase}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Simular comissão
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Produto e parceiro vêm da reserva; a regra é versionada no backend e o
                resultado é uma estimativa indicativa.
              </Typography>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={modo}
                onChange={(_, novo: Modo | null) => {
                  if (novo) setModo(novo)
                }}
                aria-label="Modo da simulação"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="reserva">Por reserva</ToggleButton>
                <ToggleButton value="campanha">Campanha de Incentivo</ToggleButton>
              </ToggleButtonGroup>

              <Stack spacing={2}>
                {erro && <Alert severity="error">{erro}</Alert>}

                {modo === 'reserva' && (
                  <>
                    {reservas === null && !erroReservas ? (
                      <EstadoDeLista estado="carregando" minHeight={96} />
                    ) : erroReservas ? (
                      <EstadoDeLista
                        estado="erro"
                        detalhe={erroReservas}
                        minHeight={96}
                        acao={{ label: 'Tentar novamente', onClick: () => void carregarReservas() }}
                      />
                    ) : reservas && reservas.length === 0 ? (
                      <EstadoDeLista
                        estado="vazio"
                        mensagem="Nenhuma reserva na sua carteira"
                        detalhe="Crie uma reserva para simular por produto, ou simule a Campanha de Incentivo."
                        minHeight={96}
                      />
                    ) : (
                      <Autocomplete<Oportunidade>
                        options={reservas ?? []}
                        value={reserva}
                        onChange={(_, nova) => setReserva(nova)}
                        getOptionLabel={rotuloReserva}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        noOptionsText="Nenhuma reserva com esse número ou empresa"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Reserva"
                            placeholder="Busque por número ou empresa"
                          />
                        )}
                      />
                    )}

                    {reserva && (
                      <Box data-testid="reserva-selecionada">
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                          {reserva.status && (
                            <Chip size="small" variant="outlined" label={STATUS_LABEL[reserva.status] ?? reserva.status} />
                          )}
                        </Stack>
                        <Linha
                          rotulo="Produto"
                          valor={
                            // P1 (AN-PP1 §3.2): pelo código OTHER_NEED ou legado sem produto.
                            reservaEhOutraNecessidade(reserva)
                              ? `${ROTULO_OUTRA_NECESSIDADE} · ${reserva.tipoNecessidade}`
                              : reserva.tipoNecessidade
                          }
                        />
                        {reservaEhOutraNecessidade(reserva) && (
                          <Alert severity="info" sx={{ my: 1 }} data-testid="aviso-outra-necessidade">
                            Sem simulação automática: a Originei avalia a necessidade descrita.
                          </Alert>
                        )}
                        {reservaCampanha && (
                          <Alert severity="info" sx={{ my: 1 }} data-testid="aviso-reserva-campanha">
                            Reserva de Campanha de Incentivo: informe a taxa negociada — a simulação usa a
                            fórmula da Campanha vigente.
                          </Alert>
                        )}
                        <Linha
                          rotulo="Parceiro"
                          valor={
                            reserva.parceiroSelecionadoNome ??
                            // P4 (Q71): o 360 simula sem parceiro.
                            (modelo360 ? PARCEIRO_NAO_EXIGIDO : 'Ainda não definido pela Originei')
                          }
                        />
                        <Typography variant="caption" color="textSecondary">
                          Produto e parceiro são definidos na reserva — não se alteram aqui.
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                <CurrencyField
                  label={modelo360 ? 'Valor da operação (opcional)' : 'Valor da operação'}
                  value={valor}
                  onChange={setValor}
                  error={!!errosCampo.valorOperacao}
                  helperText={
                    errosCampo.valorOperacao ??
                    (modelo360
                      ? 'Opcional no modelo 360: não altera a comissão. Vazio = não informado.'
                      : 'Zero é aceito: os resultados ficam zerados, sem faixa.')
                  }
                  fullWidth
                />

                {comTaxaNegociada && (
                  <TextField
                    label="Taxa negociada (%)"
                    value={taxa}
                    onChange={(e) => setTaxa(e.target.value)}
                    error={!!errosCampo.taxaNegociada}
                    helperText={errosCampo.taxaNegociada ?? 'Ex.: 2,5 para 2,5%'}
                    inputProps={{ inputMode: 'decimal' }}
                    fullWidth
                  />
                )}

                {modelo360 && (
                  <CurrencyField
                    label="Receita líquida Originei"
                    value={receitaLiquida}
                    onChange={setReceitaLiquida}
                    error={!!errosCampo.receitaLiquidaOriginei}
                    helperText={
                      errosCampo.receitaLiquidaOriginei ??
                      'Modelo 360: a comissão incide sobre a receita líquida da Originei, sem faixas.'
                    }
                    fullWidth
                  />
                )}

                {modo === 'reserva' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                      Dados de elegibilidade (opcional)
                    </Typography>
                    {preenchidoDaReserva && (
                      <Typography variant="caption" color="text.secondary" data-testid="elegibilidade-da-reserva">
                        Pré-preenchido com os dados declarados na reserva.
                      </Typography>
                    )}
                    <CurrencyField
                      label="Faturamento anual da empresa"
                      value={faturamento}
                      onChange={setFaturamento}
                      error={!!errosCampo['dadosElegibilidade.faturamentoAnual']}
                      helperText={
                        errosCampo['dadosElegibilidade.faturamentoAnual'] ??
                        'Gera alertas de elegibilidade; não bloqueia o cálculo. Vazio = não informado.'
                      }
                      fullWidth
                    />
                    <CurrencyField
                      label="Valor do imóvel (garantia)"
                      value={valorImovel}
                      onChange={setValorImovel}
                      error={!!errosCampo['dadosElegibilidade.valorImovel']}
                      helperText={
                        errosCampo['dadosElegibilidade.valorImovel'] ??
                        'Home Equity: o LTV é calculado pelo servidor (solicitado ÷ imóvel), igual à reserva. Vazio = não informado.'
                      }
                      fullWidth
                    />
                    <CurrencyField
                      label="Valor solicitado"
                      value={valorSolicitado}
                      onChange={setValorSolicitado}
                      error={!!errosCampo['dadosElegibilidade.valorSolicitado']}
                      helperText={
                        errosCampo['dadosElegibilidade.valorSolicitado'] ?? 'Crédito pedido com a garantia do imóvel.'
                      }
                      fullWidth
                    />
                  </>
                )}

                <TextField
                  label="Data de referência"
                  type="date"
                  value={dataReferencia}
                  onChange={(e) => setDataReferencia(e.target.value)}
                  error={!!errosCampo.dataReferencia}
                  helperText={errosCampo.dataReferencia ?? 'Resolve as políticas vigentes nesta data. Vazio = hoje.'}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: hojeIso() }}
                  fullWidth
                />

                <Button
                  variant="contained"
                  startIcon={<Calculate />}
                  onClick={() => void simular()}
                  disabled={!podeSimular}
                >
                  {simulando ? 'Simulando…' : 'Simular'}
                </Button>
              </Stack>

              {resultado && (
                <Box sx={{ mt: 2 }} data-testid="resultado-simulacao">
                  <Resultado resultado={resultado} reserva={reserva} />
                </Box>
              )}
            </CardContent>
          </Card>

          {modo === 'reserva' && reserva && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Simulações anteriores
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                  Snapshots imutáveis desta reserva, com as versões usadas em cada um.
                </Typography>
                {carregandoHistorico && historico === null ? (
                  <EstadoDeLista estado="carregando" minHeight={96} />
                ) : !historico || historico.length === 0 ? (
                  <EstadoDeLista
                    estado="vazio"
                    mensagem="Nenhuma simulação anterior desta reserva"
                    minHeight={96}
                  />
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="Simulações anteriores">
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="right">Líquido</TableCell>
                          <TableCell>Versões</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historico.map((h, i) => (
                          <TableRow key={h.id ?? i} hover>
                            <TableCell>{formatDateTime(h.criadoEm)}</TableCell>
                            <TableCell>
                              <ChipEstado resultado={h} />
                            </TableCell>
                            <TableCell align="right">{brl(h.valorOperacao)}</TableCell>
                            <TableCell align="right">{brl(h.valorLiquido)}</TableCell>
                            <TableCell>
                              <Typography variant="caption">{versoesDe(h)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Regras por produto
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Modelo vigente hoje de cada produto, cadastrado pela Originei com versão e origem.
                Produto sem política vigente fica bloqueado até a revisão administrativa.
              </Typography>
              {vigentes === null && !erroVigentes ? (
                <EstadoDeLista estado="carregando" />
              ) : erroVigentes ? (
                <EstadoDeLista
                  estado="erro"
                  detalhe={erroVigentes}
                  acao={{ label: 'Tentar novamente', onClick: () => void carregarVigentes() }}
                />
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Regras por produto">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell>Modelo</TableCell>
                        <TableCell>Natureza</TableCell>
                        <TableCell>Versão</TableCell>
                        <TableCell>Origem</TableCell>
                        <TableCell>Situação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linhas.map((l) => (
                        <TableRow key={l.chave} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{l.produto}</TableCell>
                          <TableCell>{l.modelo}</TableCell>
                          <TableCell>{l.natureza}</TableCell>
                          <TableCell>{l.versao}</TableCell>
                          <TableCell>{l.origem}</TableCell>
                          <TableCell>
                            {l.situacao === 'campanha' ? (
                              <Chip size="small" color="success" label={REGRA_VIGENTE} />
                            ) : l.situacao === 'vigente' ? (
                              <Chip size="small" color="success" label={POLITICA_VIGENTE} />
                            ) : l.situacao === 'indisponivel' ? (
                              <Chip size="small" color="info" variant="outlined" label={TITULO_SIMULACAO_INDISPONIVEL} />
                            ) : (
                              <Chip size="small" color="warning" variant="outlined" label={SEM_POLITICA_VIGENTE} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <FaixasVigentes
                dataReferencia={dataReferencia}
                comissao={comissaoVigente}
                erro={erroComissao}
                onTentarNovamente={() => void carregarComissao(dataReferencia)}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

// ─── P4: faixas vigentes (só leitura do backend) ───

/** Limite superior de uma faixa como TEXTO — nada calculado; o inferior é o teto da anterior. */
const limiteDaFaixa = (f: FaixaComissao): string =>
  f.valorAte == null
    ? 'Sem teto'
    : f.ateInclusivo
      ? `Até ${brl(f.valorAte)} (inclusive)`
      : `Abaixo de ${brl(f.valorAte)}`

/** Data a que as faixas se referem, por extenso: a data de referência escolhida ou hoje. */
const rotuloDataFaixas =(dataReferencia: string): string =>
  dataReferencia ? `Na data de referência ${formatarData(dataReferencia)}` : 'Vigentes hoje'

const FaixasVigentes: FC<{
  dataReferencia: string
  comissao: PoliticaComissaoResponse | null | undefined
  erro: string | null
  onTentarNovamente: () => void
}> = ({ dataReferencia, comissao, erro, onTentarNovamente }) => (
  <Box sx={{ mt: 3 }} data-testid="faixas-vigentes">
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      {TITULO_FAIXAS_VIGENTES}
    </Typography>
    <Typography variant="caption" color="textSecondary" data-testid="data-faixas" sx={{ display: 'block' }}>
      {rotuloDataFaixas(dataReferencia)}
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
      Política de comissão global, versionada pela Originei: faixas por valor da operação (produtos de
      faixa e Campanha) e o percentual dos modelos 360.
    </Typography>
    {comissao === undefined && !erro ? (
      <EstadoDeLista estado="carregando" minHeight={96} />
    ) : erro ? (
      <EstadoDeLista
        estado="erro"
        detalhe={erro}
        minHeight={96}
        acao={{ label: 'Tentar novamente', onClick: onTentarNovamente }}
      />
    ) : comissao == null ? (
      <Alert severity="warning" data-testid="sem-politica-comissao">
        <AlertTitle>{SEM_POLITICA_COMISSAO}</AlertTitle>
        As simulações calculáveis ficam bloqueadas até a Originei publicar a política de comissão. Nada é
        assumido.
      </Alert>
    ) : (
      <>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={TITULO_FAIXAS_VIGENTES}>
            <TableHead>
              <TableRow>
                <TableCell>Faixa</TableCell>
                <TableCell>Valor da operação</TableCell>
                <TableCell align="right">Percentual</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...comissao.faixas]
                .sort((a, b) => a.ordem - b.ordem)
                .map((f) => (
                  <TableRow key={f.ordem}>
                    <TableCell sx={{ fontWeight: 600 }}>{f.rotulo}</TableCell>
                    <TableCell>{limiteDaFaixa(f)}</TableCell>
                    <TableCell align="right">{pctDeFracao(f.percentual)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack spacing={0.25} sx={{ mt: 1 }}>
          <Linha
            rotulo="Sacado360 / Sacador360"
            valor={`${pctDeFracao(comissao.percentualReceitaLiquida360)} da receita líquida Originei, sem faixa`}
          />
          <Typography variant="caption" color="textSecondary">
            {[
              `Versão ${comissao.versao}`,
              `Faixas ${comissao.versaoFaixas}`,
              `Vigente desde ${formatarData(comissao.vigenciaInicio)}`,
              comissao.vigenciaFim ? `até ${formatarData(comissao.vigenciaFim)}` : null,
              `Origem: ${comissao.origem}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Stack>
      </>
    )}
  </Box>
)

// ─── resultado por estado ───

const ehLiquidoPendente = (r: SimulacaoComissaoResponse): boolean =>
  r.situacaoLiquido === 'PENDENTE_CONFIGURACAO_TRIBUTARIA'

const ROTULO_TIPO_POLITICA: Record<string, string> = {
  PRODUTO: 'Política',
  COMISSAO: 'Comissão',
  SPREAD: 'Spread',
  ELEGIBILIDADE: 'Elegibilidade',
  TRIBUTO: 'Tributo',
  CHECKLIST: 'Checklist',
  CATALOGO: 'Catálogo',
}

/** P4: política aplicada com a vigência gravada no snapshot (nula em linha antiga). */
const politicaAplicadaTexto = (p: PoliticaAplicada): string | null => {
  if (!p.versao) return null
  const vigencia = p.vigenciaInicio
    ? ` (vigência ${formatarData(p.vigenciaInicio)}${p.vigenciaFim ? ` a ${formatarData(p.vigenciaFim)}` : ''})`
    : ''
  return `${ROTULO_TIPO_POLITICA[p.tipo] ?? p.tipo} ${p.versao}${vigencia}`
}

const versoesDe = (r: SimulacaoComissaoResponse): string =>
  (r.politicasAplicadas ?? []).length > 0
    ? [
        ...(r.politicasAplicadas ?? []).map(politicaAplicadaTexto),
        r.versaoFaixas && `Faixas ${r.versaoFaixas}`,
        r.versaoRegra && `Regra ${r.versaoRegra}`,
        r.dataReferencia && `Data de referência ${formatarData(r.dataReferencia)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : versoesAntigas(r)

/** Snapshot anterior à P4 (sem `politicasAplicadas`): só as colunas de versão, sem vigência. */
const versoesAntigas = (r: SimulacaoComissaoResponse): string =>
  [
    r.versaoPolitica && `Política ${r.versaoPolitica}`,
    r.versaoPoliticaComissao && `Comissão ${r.versaoPoliticaComissao}`,
    r.versaoSpread && `Spread ${r.versaoSpread}`,
    r.versaoTributos && `Tributos ${r.versaoTributos}`,
    r.versaoFaixas && `Faixas ${r.versaoFaixas}`,
    r.versaoRegra && `Regra ${r.versaoRegra}`,
    r.versaoElegibilidade && `Elegibilidade ${r.versaoElegibilidade}`,
    r.dataReferencia && `Data de referência ${formatarData(r.dataReferencia)}`,
  ]
    .filter(Boolean)
    .join(' · ') || '—'

/** `2026-10-21` → `21/10/2026` sem passar por Date (sem fuso). */
const formatarData = (iso: string): string => {
  const [a, m, d] = iso.split('-')
  return d && m && a ? `${d}/${m}/${a}` : iso
}

const QuemCorrige: FC<{ responsavel: string | null | undefined }> = ({ responsavel }) =>
  responsavel ? (
    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
      Quem corrige: {responsavel}
    </Typography>
  ) : null

/**
 * P4: todos os motivos do bloqueio; snapshot antigo (lista vazia/ausente) vira um item a partir dos
 * campos de sempre. O primeiro é o principal (decide o título).
 */
const motivosDe = (r: SimulacaoComissaoResponse): MotivoBloqueioDetalhe[] =>
  r.motivosBloqueio && r.motivosBloqueio.length > 0
    ? r.motivosBloqueio
    : r.motivoBloqueio
      ? [
          {
            codigo: r.motivoBloqueio,
            mensagem: r.mensagemBloqueio,
            responsavelCorrecao: r.responsavelCorrecao ?? null,
          },
        ]
      : []

const quemCorrigeDe = (m: MotivoBloqueioDetalhe | undefined): string | null =>
  m?.responsavelCorrecao || (m?.responsavel ? ROTULO_RESPONSAVEL[m.responsavel] : null) || null

const OQueFazer: FC<{ acao: string | null | undefined }> = ({ acao }) =>
  acao ? (
    <Typography variant="caption" component="div">
      O que fazer: {acao}
    </Typography>
  ) : null

/** Quem corrige e o que fazer do principal + os demais motivos, cada um com os seus. */
const DetalheMotivos: FC<{ motivos: MotivoBloqueioDetalhe[] }> = ({ motivos }) => {
  const [principal, ...outros] = motivos
  return (
    <>
      <QuemCorrige responsavel={quemCorrigeDe(principal)} />
      <OQueFazer acao={principal?.acaoRequerida} />
      {outros.length > 0 && (
        <Box sx={{ mt: 1 }} data-testid="outros-motivos">
          <Typography variant="caption" component="div" sx={{ fontWeight: 700 }}>
            {TITULO_OUTROS_MOTIVOS}:
          </Typography>
          {outros.map((m, i) => (
            <Box key={`${m.codigo}-${i}`} sx={{ mt: 0.5 }} data-testid="motivo-bloqueio">
              <Typography variant="body2">
                {m.mensagem ??
                  (m.codigo === 'POLITICA_COMISSAO_AUSENTE' ? MENSAGEM_POLITICA_COMISSAO_AUSENTE : m.codigo)}
              </Typography>
              <Typography variant="caption" component="div" color="textSecondary">
                Motivo: {m.codigo}
              </Typography>
              <QuemCorrige responsavel={quemCorrigeDe(m)} />
              <OQueFazer acao={m.acaoRequerida} />
            </Box>
          ))}
        </Box>
      )}
    </>
  )
}

const ChipEstado: FC<{ resultado: SimulacaoComissaoResponse }> = ({ resultado: r }) =>
  r.estado === 'CALCULADA' ? (
    <Chip size="small" color="success" label="Calculada" />
  ) : (
    <Chip size="small" color="warning" variant="outlined" label={r.motivoBloqueio ?? r.estado} />
  )

const mailtoRevisao = (r: SimulacaoComissaoResponse, reserva: Oportunidade | null): string => {
  const assunto = 'Revisão administrativa — simulação bloqueada'
  const motivos = motivosDe(r)
  const corpo = [
    `Motivo: ${motivos.length > 0 ? motivos.map((m) => m.codigo).join(', ') : r.estado}`,
    reserva ? `Reserva: ${rotuloReserva(reserva)}` : null,
    r.produtoNome ? `Produto: ${r.produtoNome}` : null,
    r.parceiroNome ? `Parceiro: ${r.parceiroNome}` : null,
    ...motivos.map((m) => m.mensagem),
  ]
    .filter(Boolean)
    .join('\n')
  return `mailto:?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`
}

const Resultado: FC<{ resultado: SimulacaoComissaoResponse; reserva: Oportunidade | null }> = ({
  resultado: r,
  reserva,
}) => {
  // P4: o motivo principal é `motivosBloqueio[0]` (coincide com `motivoBloqueio` por contrato);
  // snapshot antigo sem a lista cai para os campos de sempre.
  const motivos = motivosDe(r)
  const principal = motivos[0]
  const mensagemPrincipal = principal?.mensagem ?? r.mensagemBloqueio
  const motivo = (principal?.codigo ?? r.motivoBloqueio) as MotivoBloqueio | null

  // P1 (AN-PP1 §3.2): "aguarde a sincronização" vale em qualquer estado — inclusive a linha
  // PARCIAL de transferência sem produto, que antes saía como OUTRA_NECESSIDADE.
  if (motivo === 'RESERVA_NAO_SINCRONIZADA') {
    return (
      <Alert severity="warning">
        <AlertTitle>{TITULO_RESERVA_NAO_SINCRONIZADA}</AlertTitle>
        {mensagemPrincipal || MENSAGEM_RESERVA_NAO_SINCRONIZADA}
        <DetalheMotivos motivos={motivos} />
      </Alert>
    )
  }

  if (r.estado === 'BLOQUEADA_PARCEIRO') {
    return (
      <Alert severity="warning">
        <AlertTitle>{TITULO_BLOQUEIO_PARCEIRO}</AlertTitle>
        {mensagemPrincipal}
        <DetalheMotivos motivos={motivos} />
      </Alert>
    )
  }

  if (r.estado === 'BLOQUEADA_POLITICA') {
    if (motivo === 'OUTRA_NECESSIDADE') {
      return (
        <Alert severity="info">
          <AlertTitle>{TITULO_OUTRA_NECESSIDADE}</AlertTitle>
          {mensagemPrincipal ?? MENSAGEM_OUTRA_NECESSIDADE}
          <OQueFazer acao={principal?.acaoRequerida} />
        </Alert>
      )
    }
    if (motivo && MOTIVOS_INDISPONIVEL.has(motivo)) {
      // CAPEX (e snapshot antigo da Duplicata, retirada): indisponibilidade é o comportamento correto (§12), não falha.
      return (
        <Alert severity="info">
          <AlertTitle>{TITULO_SIMULACAO_INDISPONIVEL}</AlertTitle>
          {mensagemPrincipal}
          <DetalheMotivos motivos={motivos} />
        </Alert>
      )
    }
    if (motivo === 'PRODUTO_RETIRADO') {
      // Duplicata Escritural (Políticas Vigentes 25/10, seção 8): retirada é decisão, não falha.
      return (
        <Alert severity="info">
          <AlertTitle>{TITULO_PRODUTO_RETIRADO}</AlertTitle>
          {mensagemPrincipal}
          <DetalheMotivos motivos={motivos} />
        </Alert>
      )
    }
    if (motivo === 'RESERVA_ENCERRADA') {
      return (
        <Alert severity="info">
          <AlertTitle>{TITULO_RESERVA_ENCERRADA}</AlertTitle>
          {mensagemPrincipal}
        </Alert>
      )
    }
    // POLITICA_PRODUTO_AUSENTE · SEM_FORMULA · SPREAD_NAO_VIGENTE · POLITICA_COMISSAO_AUSENTE (P4)
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" href={mailtoRevisao(r, reserva)}>
            {SOLICITAR_REVISAO}
          </Button>
        }
      >
        <AlertTitle>{TITULO_BLOQUEIO_POLITICA}</AlertTitle>
        {mensagemPrincipal ||
          (motivo === 'POLITICA_COMISSAO_AUSENTE' ? MENSAGEM_POLITICA_COMISSAO_AUSENTE : null)}
        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
          Motivo: {motivo ?? '—'}. Nada é assumido: sem política vigente não há resultado.
        </Typography>
        <DetalheMotivos motivos={motivos} />
      </Alert>
    )
  }

  // CALCULADA
  const pendente = ehLiquidoPendente(r)
  const todos = r.alertas ?? []
  const alertasPolitica = todos.filter((a) => a.codigo === ALERTA_CAMPANHA_NAO_APLICAVEL)
  const alertas = todos.filter((a) => a.codigo !== ALERTA_CAMPANHA_NAO_APLICAVEL)
  // P4 (§6): no 360 a base é a receita líquida Originei (snapshot antigo: pelo modelo).
  const eh360 = r.modeloComissao === 'NET_ORIGINEI_REVENUE_15' || r.modelo === 'RECEITA_LIQUIDA_360'
  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>{AVISO_INDICATIVA}</AlertTitle>
        {r.aviso}
      </Alert>
      {alertasPolitica.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }} data-testid="alertas-politica">
          <AlertTitle>{TITULO_ALERTAS_POLITICA}</AlertTitle>
          {alertasPolitica.map((a) => (
            <Typography key={a.codigo} variant="body2">
              {a.mensagem}
            </Typography>
          ))}
        </Alert>
      )}
      {alertas.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }} data-testid="alertas-elegibilidade">
          <AlertTitle>
            {TITULO_ALERTAS_ELEGIBILIDADE}{' '}
            {/* P2 (Q24/Q67): rota Grandes Operações — pela base do valor da operação; não muda a fórmula. */}
            {alertas.some((a) => a.codigo === CODIGOS_ELEGIBILIDADE.ENCAMINHAR_GRANDES_OPERACOES) && (
              <ChipRotaOperacao rota="GRANDES_OPERACOES" />
            )}
          </AlertTitle>
          {alertas.map((a) => (
            <Typography key={a.codigo} variant="body2">
              {a.mensagem}
            </Typography>
          ))}
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            Não bloqueiam o cálculo. Elegibilidade não equivale a aprovação de crédito; o impedimento existe só
            no encaminhamento ao parceiro.
          </Typography>
        </Alert>
      )}
      <Stack spacing={0.5}>
        <Linha rotulo="Produto" valor={r.modelo === 'CAMPANHA' ? 'Campanha de Incentivo' : r.produtoNome ?? '—'} />
        {r.parceiroNome && <Linha rotulo="Parceiro" valor={r.parceiroNome} />}
        <Linha rotulo="Natureza" valor={r.natureza ?? '—'} />
        {/* P4 (Q72): 360 sem valor da operação → a linha some. */}
        {!(eh360 && r.valorOperacao == null) && (
          <Linha rotulo="Valor da operação" valor={brl(r.valorOperacao)} />
        )}
        {r.spread != null && <Linha rotulo="Spread" valor={pctDeFracao(r.spread)} />}
        {/* Modelos 360 não usam faixa de volume (§2.3/§7): a linha nem aparece. */}
        {!eh360 && (
          <Linha
            rotulo="Faixa"
            valor={r.faixa ? `${r.faixa} (${pctDeFracao(r.percentualFaixa)})` : SEM_FAIXA}
          />
        )}
        <Linha rotulo={eh360 ? ROTULO_RECEITA_LIQUIDA : 'Receita Originei'} valor={brl(r.receitaOriginei)} />
        <Linha rotulo="Comissão bruta" valor={brl(r.comissaoBruta)} />
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {ROTULO_RETENCOES}
        </Typography>
        {(r.tributos ?? []).length === 0 ? (
          <Typography variant="caption" color="textSecondary">
            {pendente ? 'Nenhuma alíquota vigente na data de referência.' : 'Nenhum tributo aplicado.'}
          </Typography>
        ) : (
          (r.tributos ?? []).map((t, i) => (
            <Linha
              key={`${t.nome}-${i}`}
              rotulo={`${t.nome}${t.descricao ? ` — ${t.descricao}` : ''} (${pctDeFracao(t.aliquota)})`}
              valor={`− ${brl(t.valor)}`}
            />
          ))
        )}
        <Divider sx={{ my: 0.5 }} />
        <Linha
          rotulo="Valor líquido (indicativo)"
          valor={pendente ? LIQUIDO_PENDENTE : brl(r.valorLiquido)}
          destaque
        />
        {pendente &&
          (r.pendencias ?? []).map((p) => (
            <Alert key={p.codigo} severity="info" sx={{ mt: 0.5 }} data-testid="pendencia-liquido">
              {p.mensagem}
              <QuemCorrige responsavel={p.responsavel} />
            </Alert>
          ))}
        <ComoFoiCalculado passos={r.passos} memoria={r.memoriaCalculo} />
        <Typography variant="caption" color="textSecondary">
          {versoesDe(r)}
        </Typography>
      </Stack>
    </>
  )
}

/** Valor de um passo na unidade que o backend declarou — só formatação, nada calculado. */
const valorDoPasso = (p: PassoCalculo): string =>
  p.valor == null ? '—' : p.unidade === 'FRACAO' ? pctDeFracao(p.valor) : brl(p.valor)

/**
 * P4 (§7): fórmula explicável estruturada, na ordem do backend. Snapshot anterior à P4 (sem passos)
 * mostra só a `memoriaCalculo` em texto.
 */
const ComoFoiCalculado: FC<{ passos: PassoCalculo[] | null | undefined; memoria: string | null }> = ({
  passos,
  memoria,
}) => {
  const lista = [...(passos ?? [])].sort((a, b) => a.ordem - b.ordem)
  if (lista.length === 0 && !memoria) return null
  return (
    <Box sx={{ mt: 1 }} data-testid="como-foi-calculado">
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {TITULO_COMO_CALCULADO}
      </Typography>
      {lista.length > 0 ? (
        <Table size="small" aria-label={TITULO_COMO_CALCULADO}>
          <TableBody>
            {lista.map((p) => (
              <TableRow key={`${p.ordem}-${p.codigo}`} data-testid={`passo-${p.codigo}`}>
                <TableCell sx={{ pl: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p.rotulo}
                  </Typography>
                  {p.formula && (
                    <Typography variant="caption" color="textSecondary" component="div">
                      {p.formula}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ pr: 0 }}>
                  {valorDoPasso(p)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          sx={{ whiteSpace: 'pre-wrap' }}
          data-testid="memoria-calculo"
        >
          {memoria}
        </Typography>
      )}
    </Box>
  )
}

const Linha: FC<{ rotulo: string; valor: string; destaque?: boolean }> = ({
  rotulo,
  valor,
  destaque,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="textSecondary">
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: destaque ? 700 : 500, textAlign: 'right' }}>
      {valor}
    </Typography>
  </Stack>
)

export default SimulacoesPage
